"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState, useTransition } from "react";
import { toast } from "sonner";
import {
  cancelConnectionRequest,
  cancelConnectionRequestToUser,
  sendBuddyRequest,
} from "@/actions/buddy";
import { getOrCreateDirectChat } from "@/actions/chat";
import { useProfileGatedAction } from "@/components/auth";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useBackAwarePush } from "@/hooks/use-back-aware-push";
import { updateDiscoveryRelationship } from "@/lib/buddy/discovery-cache";
import type { BuddyRelationship } from "@/lib/buddy/relationship";
import {
  invalidateBuddyRelationships,
  setBuddyRelationship,
} from "@/lib/buddy/relationship-cache";
import { buddyRelationshipOptions } from "@/lib/buddy/relationship-query";
import { uiHaptics } from "@/lib/ui-haptics";
import { cn } from "@/lib/utils";

/**
 * Where the button is mounted. Analytics only — this is how we learn which
 * cards actually produce connections now that quick-add is on all of them.
 */
export type ConnectSurface =
  | "discover_grid"
  | "recommendation_card"
  | "suggested_card"
  | "profile"
  | "event_people"
  | "post_likes"
  | "comment_likes"
  | "search"
  | "hiive_members"
  | "event_members"
  | "chat_members";

/**
 * How the button looks. Behaviour is identical across appearances.
 *
 * - `glass` (default): the liquid-glass pill, built to sit over photos.
 * - `solid`: the Instagram "Follow" treatment for flat list rows — a filled
 *   brand button while there is something to do (Connect, Respond) that
 *   settles into a neutral grey once the ball is in the other court
 *   (Requested) or the two are already buddies (Message). One fixed width for
 *   every state, so the row never reflows when it flips.
 */
export type ConnectButtonAppearance = "glass" | "solid";

export type ConnectButtonProps = {
  targetUserId: string;
  targetName?: string | null;
  /**
   * What we already know about the relationship. Lists should pass this from a
   * batched `resolveBuddyRelationships` call rather than letting each button
   * discover it on its own.
   *
   * `undefined` means "not resolved yet" — the button renders a same-size
   * placeholder instead of guessing, because guessing `"none"` shows an "Add"
   * button to someone who is already a buddy.
   */
  relationship?: BuddyRelationship;
  surface: ConnectSurface;
  size?: "sm" | "default";
  appearance?: ConnectButtonAppearance;
  className?: string;
  /**
   * Rendered instead of the button once the two are already buddies. Leaving
   * it out renders nothing for `glass`, and a neutral "Message" button for
   * `solid` (pass `null` explicitly to render nothing there too).
   */
  buddyFallback?: React.ReactNode;
  onNavigate?: () => void;
  onRelationshipChange?: (next: BuddyRelationship) => void;
};

const UNDO_TOAST_DURATION_MS = 6_000;

/**
 * The one liquid-glass surface every state of this button uses.
 *
 * Real glass, not a tinted slab: the brand colour is carried at low alpha so
 * whatever sits behind — a portrait, a card, the page — reads straight through
 * it. `backdrop-saturate` is what sells that; without the saturation boost the
 * show-through looks like a flat wash rather than something you are seeing the
 * world through. The hairline border plus the inset top highlight are the lit
 * glass edge, and the sheen span below supplies the falloff.
 *
 * Deliberately identical across Add / Requested / Respond — the state is
 * carried by the icon and the label, never by a colour change.
 */
const LIQUID_GLASS = cn(
  "group relative shrink-0 overflow-hidden",
  "border border-white/30 bg-brand/25 text-white",
  "backdrop-blur-xl backdrop-saturate-150",
  "shadow-[inset_0_1px_0_rgba(255,255,255,0.5),inset_0_-1px_0_rgba(255,255,255,0.1),0_6px_20px_-6px_rgba(0,0,0,0.45)]",
  "transition-[transform,background-color,box-shadow] duration-200 ease-out-strong",
  "hover:bg-brand/40",
  "active:scale-[0.94] active:shadow-[inset_0_2px_7px_rgba(0,0,0,0.35)]",
  "dark:border-white/25 dark:bg-brand/25 dark:hover:bg-brand/40",
);

/** Keeps the label legible when the glass is sitting over a bright photo. */
const GLASS_LABEL =
  "relative z-10 font-semibold [text-shadow:0_1px_3px_rgba(0,0,0,0.55)]";

/**
 * The specular sheen across the top half. Separate from the fill so it stays
 * crisp when the translucent background picks up a dark photo behind it.
 */
function GlassSheen() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/30 to-transparent"
    />
  );
}

/**
 * The `solid` appearance. Built on the `ghost` variant because it carries the
 * least chrome to undo: no border, blur or inset shadow. `w-[6.5rem]` is one
 * width for every label ("Requested" is the longest), so the flip from
 * Connect to Requested is a colour change, not a reflow. `disabled:opacity-100`
 * because a pending Connect shows a spinner instead of fading out.
 */
const SOLID_BASE = cn(
  "relative h-8 w-[6.5rem] shrink-0 overflow-hidden rounded-lg px-3",
  "text-[13px] leading-none font-semibold",
  "shadow-none backdrop-blur-none",
  "transition-[background-color,color,transform] duration-150 ease-out-strong",
  "active:scale-[0.97] disabled:opacity-100 pointer-fine:hover:scale-100",
);

const SOLID_TONE = {
  // `dark:` twins are required: a plain `bg-brand` loses to the variant's own
  // `dark:` background, which is how brand buttons end up grey in dark mode.
  primary:
    "bg-brand text-white hover:bg-brand-hover dark:bg-brand dark:text-white dark:hover:bg-brand-hover",
  neutral:
    "bg-black/[0.06] text-text-primary hover:bg-black/[0.1] dark:bg-white/[0.12] dark:text-white dark:hover:bg-white/[0.18]",
} as const;

/**
 * Whether this device has a real pointer — the JS mirror of the
 * `pointer-fine:` variant used for the hover label swap a few lines below.
 * Keep the two in step: they must agree on who gets hover affordances.
 */
function useHasFinePointer() {
  const [isFine, setIsFine] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(pointer: fine)");
    const sync = () => setIsFine(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  return isFine;
}

export function ConnectButton({
  targetUserId,
  targetName,
  relationship: initialRelationship,
  surface,
  size = "sm",
  appearance = "glass",
  className,
  buddyFallback,
  onRelationshipChange,
  onNavigate,
}: ConnectButtonProps) {
  const ensureCanConnect = useProfileGatedAction({
    title: "Sign in to connect",
    description: "Use your Hiive account to send buddy requests.",
  });
  const queryClient = useQueryClient();
  const router = useRouter();
  const backAwarePush = useBackAwarePush();
  const hasFinePointer = useHasFinePointer();
  const { confirm, confirmDialog } = useConfirm();
  const relationshipQuery = useQuery(
    buddyRelationshipOptions(queryClient, targetUserId, initialRelationship),
  );
  const relationship = relationshipQuery.data ?? initialRelationship;
  const [isPending, startTransition] = useTransition();
  const [isOpeningChat, startOpeningChat] = useTransition();

  const firstName = targetName?.trim().split(/\s+/)[0];

  const applyRelationship = useCallback(
    (next: BuddyRelationship) => {
      setBuddyRelationship(queryClient, targetUserId, next);
      updateDiscoveryRelationship(queryClient, targetUserId, next);
      onRelationshipChange?.(next);
    },
    [onRelationshipChange, queryClient, targetUserId],
  );

  const invalidateRelationshipQueries = useCallback(() => {
    invalidateBuddyRelationships(queryClient, targetUserId);
    void queryClient.invalidateQueries({ queryKey: ["connection-requests"] });
  }, [queryClient, targetUserId]);

  const handleUndo = useCallback(
    (requestId: number) => {
      // Flip back first; a failed cancel re-asserts the sent state below.
      uiHaptics.tap();
      applyRelationship("none");

      startTransition(async () => {
        const result = await cancelConnectionRequest(requestId);

        if (!result.ok) {
          applyRelationship("request_sent");
          invalidateRelationshipQueries();
          toast.error(result.message ?? "Couldn't undo that");
          return;
        }

        invalidateRelationshipQueries();
      });
    },
    [applyRelationship, invalidateRelationshipQueries],
  );

  /**
   * Tap "Requested" to withdraw — the gesture people already expect from
   * Instagram. Distinct from the toast's Undo, which still has the fresh
   * `requestId`; this one resolves the pending row from the target alone,
   * because after a reload the card has no id to work from.
   */
  const handleUnsend = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      // On a touch device there is no hover, so nothing has told this person
      // the button is even actionable. The confirm is what carries that —
      // it is the only place the option to withdraw becomes visible. With a
      // real pointer the label has already swapped to "Withdraw", so acting
      // straight away is both clear and faster.
      if (!hasFinePointer) {
        const confirmed = await confirm({
          title: firstName
            ? `Withdraw your request to ${firstName}?`
            : "Withdraw your request?",
          description: "They won't be told. You can send a new request later.",
          confirmLabel: "Withdraw",
          cancelLabel: "Keep it",
        });

        if (!confirmed) {
          return;
        }
      }

      uiHaptics.tap();
      applyRelationship("none");

      startTransition(async () => {
        const result = await cancelConnectionRequestToUser(targetUserId);

        if (!result.ok) {
          applyRelationship("request_sent");
          invalidateRelationshipQueries();
          toast.error(result.message ?? "Couldn't withdraw that request");
          return;
        }

        invalidateRelationshipQueries();
        toast.success(
          firstName ? `Request to ${firstName} withdrawn` : "Request withdrawn",
        );
      });
    },
    [
      applyRelationship,
      confirm,
      firstName,
      hasFinePointer,
      invalidateRelationshipQueries,
      targetUserId,
    ],
  );

  const handleConnect = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // Most of these buttons sit inside a card that is itself a link to the
      // profile. Adding must not also navigate.
      event.preventDefault();
      event.stopPropagation();

      // Opens the sign-in or finish-profile prompt itself when it returns false.
      if (!ensureCanConnect()) {
        return;
      }

      // Felt with the optimistic flip, not after the round trip.
      uiHaptics.success();
      applyRelationship("request_sent");

      startTransition(async () => {
        const result = await sendBuddyRequest({
          targetUserId,
          intent: "direct",
          surface,
        });

        if (!result.ok) {
          applyRelationship("none");
          invalidateRelationshipQueries();
          uiHaptics.error();
          toast.error(result.message ?? "We couldn't send that request");
          return;
        }

        applyRelationship("request_sent");
        invalidateRelationshipQueries();

        const requestId = result.data.requestId;
        toast.success(
          firstName ? `Request sent to ${firstName}` : "Request sent",
          {
            duration: UNDO_TOAST_DURATION_MS,
            action: {
              label: "Undo",
              onClick: () => handleUndo(requestId),
            },
          },
        );
      });
    },
    [
      applyRelationship,
      ensureCanConnect,
      firstName,
      handleUndo,
      invalidateRelationshipQueries,
      surface,
      targetUserId,
    ],
  );

  const handleMessage = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      startOpeningChat(async () => {
        const result = await getOrCreateDirectChat(targetUserId);
        if (result.error || !result.data) {
          toast.error(result.error ?? "Couldn't open that chat");
          return;
        }
        onNavigate?.();
        backAwarePush(`/chat/${result.data.id}`);
      });
    },
    [backAwarePush, onNavigate, targetUserId],
  );

  if (appearance === "solid") {
    // Not resolved yet: same box, invisible (see the glass placeholder below).
    if (relationship === undefined) {
      return (
        <Button
          type="button"
          variant="ghost"
          disabled
          aria-hidden
          tabIndex={-1}
          className={cn(SOLID_BASE, "invisible", className)}
        >
          Connect
        </Button>
      );
    }

    if (relationship === "self" || relationship === "blocked") {
      return null;
    }

    if (relationship === "buddy" && buddyFallback !== undefined) {
      return <>{buddyFallback}</>;
    }

    const solidState = (() => {
      switch (relationship) {
        case "buddy":
          return {
            tone: "neutral" as const,
            label: "Message",
            hoverLabel: null,
            busy: isOpeningChat,
            ariaLabel: firstName ? `Message ${firstName}` : "Message",
            onClick: handleMessage,
          };
        case "request_received":
          return {
            tone: "primary" as const,
            label: "Respond",
            hoverLabel: null,
            busy: false,
            ariaLabel: firstName
              ? `Respond to ${firstName}'s request`
              : "Respond to their request",
            onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
              event.preventDefault();
              event.stopPropagation();
              router.push("/connection-requests");
            },
          };
        case "request_sent":
          return {
            tone: "neutral" as const,
            label: "Requested",
            hoverLabel: "Withdraw",
            busy: false,
            ariaLabel: firstName
              ? `Requested — tap to withdraw your request to ${firstName}`
              : "Requested — tap to withdraw",
            onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
              void handleUnsend(event);
            },
          };
        default:
          return {
            tone: "primary" as const,
            label: "Connect",
            hoverLabel: null,
            // Only after an undo/withdraw: the optimistic flip to Requested
            // happens before this could ever show.
            busy: isPending,
            ariaLabel: firstName
              ? `Connect with ${firstName}`
              : "Send a buddy request",
            onClick: handleConnect,
          };
      }
    })();

    // ONE element for every state, so the flip is a background transition on
    // the same node rather than one button unmounting under another.
    return (
      <>
        <Button
          type="button"
          variant="ghost"
          disabled={solidState.busy}
          aria-busy={solidState.busy || undefined}
          aria-label={solidState.ariaLabel}
          onClick={solidState.onClick}
          className={cn(
            SOLID_BASE,
            SOLID_TONE[solidState.tone],
            "group",
            className,
          )}
        >
          <span
            // Keyed so a state change fades the new word in instead of
            // snapping; the box itself never changes size.
            key={solidState.label}
            className={cn(
              "animate-in duration-150 fade-in-0",
              solidState.busy && "invisible",
              solidState.hoverLabel && "pointer-fine:group-hover:hidden",
            )}
          >
            {solidState.label}
          </span>
          {solidState.hoverLabel ? (
            <span className="hidden pointer-fine:group-hover:inline">
              {solidState.hoverLabel}
            </span>
          ) : null}
          {solidState.busy ? (
            <Loader2
              aria-hidden
              className="absolute inset-0 m-auto size-4 animate-spin"
            />
          ) : null}
        </Button>
        {confirmDialog}
      </>
    );
  }

  // Not resolved yet. Hold the row's width with an inert placeholder so the
  // layout does not shift when the real state arrives.
  if (relationship === undefined) {
    return (
      <Button
        type="button"
        size={size}
        disabled
        aria-hidden
        tabIndex={-1}
        // `invisible`, not `opacity-0`: the base button's `disabled:opacity-50`
        // would otherwise win the cascade and leave a ghost button on screen.
        className={cn("invisible shrink-0", className)}
      >
        Connect
      </Button>
    );
  }

  if (relationship === "self" || relationship === "blocked") {
    return null;
  }

  if (relationship === "buddy") {
    return <>{buddyFallback ?? null}</>;
  }

  // They asked first. Sending a second request into the same pair would hit the
  // one-pending-pair unique index, so point at the inbox instead.
  if (relationship === "request_received") {
    return (
      <Button
        type="button"
        size={size}
        className={cn(LIQUID_GLASS, className)}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          router.push("/connection-requests");
        }}
      >
        <GlassSheen />
        <span className={GLASS_LABEL}>Respond</span>
      </Button>
    );
  }

  if (relationship === "request_sent") {
    // Tappable, not a dead status chip: this is the only place most people
    // will look to take a request back. It stays a real button (never
    // `disabled`) so it keeps full contrast on a photo — `disabled:opacity-50`
    // would fade it close to invisible on the card surfaces.
    return (
      <>
        <Button
          type="button"
          size={size}
          onClick={(event) => {
            void handleUnsend(event);
          }}
          aria-label={
            firstName
              ? `Requested — tap to withdraw your request to ${firstName}`
              : "Requested — tap to withdraw"
          }
          className={cn(LIQUID_GLASS, className)}
        >
          <GlassSheen />
          {/* With a real pointer, hovering says plainly what the tap does. On
              touch there is no hover, so the label stays "Requested" and the
              confirm dialog in `handleUnsend` is what surfaces the option. */}
          <span className={cn(GLASS_LABEL, "pointer-fine:group-hover:hidden")}>
            Requested
          </span>
          <span
            className={cn(
              GLASS_LABEL,
              "hidden pointer-fine:group-hover:inline",
            )}
          >
            Withdraw
          </span>
        </Button>
        {confirmDialog}
      </>
    );
  }

  return (
    <Button
      type="button"
      size={size}
      disabled={isPending}
      onClick={handleConnect}
      aria-label={
        firstName ? `Connect with ${firstName}` : "Send a buddy request"
      }
      className={cn(LIQUID_GLASS, className)}
    >
      <GlassSheen />
      <span className={GLASS_LABEL}>Connect</span>
    </Button>
  );
}
